import { VENDOR_SERVICE_PRICING } from "../marketplacePricing";

export type CountryCode = "IN" | "AE";
export type IntegrationType = "EXTERNAL_LINK" | "IN_APP_FORM" | "EMBEDDED_UI";

export interface MarketplaceCategory {
  id: string;
  slug: string;
  name: string;
  description: string;
  icon: string;
  subCategories: string[];
  sortOrder: number;
}

export interface MarketplaceService {
  name: string;
  description: string;
  mrp: number;
  priceUnit: string;
}

export interface MarketplaceAppDownload {
  appName?: string;
  universalUrl?: string;
  iosUrl?: string;
  androidUrl?: string;
}

export interface MarketplaceVendor {
  id: string;
  name: string;
  description: string;
  longDescription: string;
  logo: string;
  countries: CountryCode[];
  integrationType: IntegrationType;
  externalUrl?: string;
  embeddedType?: "wellness" | "doctor-online" | "vehicle-care";
  subCategories: string[];
  services: MarketplaceService[];
  appDownload?: MarketplaceAppDownload;
  isPlatformVendor?: boolean;
  tags?: string[];
}

export interface CategoryVendorListing {
  categoryId: string;
  vendorId: string;
  highlight: string;
}

export const COUNTRIES: { code: CountryCode; label: string; flag: string }[] = [
  { code: "IN", label: "India", flag: "🇮🇳" },
  { code: "AE", label: "Dubai", flag: "🇦🇪" },
];

export const CATEGORIES: MarketplaceCategory[] = [
  {
    id: "cat-wellness",
    slug: "wellness",
    name: "Wellness",
    description: "Holistic health programs, screenings, and corporate wellness",
    icon: "🌿",
    subCategories: ["Yoga & Meditation", "Stress Management", "Sleep & Recovery", "Health Screenings", "Wellness Programs"],
    sortOrder: 1,
  },
  {
    id: "cat-fitness",
    slug: "fitness",
    name: "Fitness",
    description: "Gyms, personal training, and fitness rewards",
    icon: "💪",
    subCategories: ["Gym Memberships", "Personal Training", "Group Classes", "Sports & Outdoor", "At-home Fitness Apps"],
    sortOrder: 2,
  },
  {
    id: "cat-telehealth",
    slug: "telehealth",
    name: "Telehealth",
    description: "Online doctor consultations and medical advice",
    icon: "🩺",
    subCategories: ["General Physician", "Gynaecology", "Pediatrics", "Dermatology", "Psychiatry", "Specialist Consultations"],
    sortOrder: 3,
  },
  {
    id: "cat-vehicle-care",
    slug: "automotives",
    name: "Automotives",
    description: "Car servicing, repairs, and roadside assistance for your vehicle",
    icon: "🚗",
    subCategories: ["Periodic Service", "AC Service & Repair", "Batteries", "Tyre & Wheel Care", "Denting & Painting", "Roadside Assistance"],
    sortOrder: 4,
  },
  {
    id: "cat-mental-health",
    slug: "mental-health",
    name: "Mental Health",
    description: "Counselling, EAP, and emotional wellbeing support",
    icon: "🧠",
    subCategories: ["Counselling & Therapy", "Employee Assistance Program", "Mindfulness Apps", "Crisis Helplines"],
    sortOrder: 5,
  },
  {
    id: "cat-nutrition",
    slug: "nutrition-diet",
    name: "Nutrition & Diet",
    description: "Dietician consults, meal plans, and healthy eating",
    icon: "🥗",
    subCategories: ["Dietician Consultations", "Meal Planning", "Weight Management", "Healthy Meal Delivery"],
    sortOrder: 6,
  },
  {
    id: "cat-dental",
    slug: "dental-care",
    name: "Dental Care",
    description: "Dental check-ups, orthodontics, and emergency care",
    icon: "🦷",
    subCategories: ["Routine Check-ups", "Orthodontics", "Cosmetic Dentistry", "Emergency Dental"],
    sortOrder: 7,
  },
  {
    id: "cat-vision",
    slug: "vision-care",
    name: "Vision Care",
    description: "Eye exams, lenses, and vision correction",
    icon: "👁️",
    subCategories: ["Eye Exams", "Spectacle Subsidies", "LASIK & Surgery", "Vision Therapy"],
    sortOrder: 8,
  },
  {
    id: "cat-financial",
    slug: "financial-wellness",
    name: "Financial Wellness",
    description: "Financial planning, tax, and retirement guidance",
    icon: "💰",
    subCategories: ["Financial Planning", "Tax Advisory", "Investment Guidance", "Retirement Planning"],
    sortOrder: 9,
  },
  {
    id: "cat-legal",
    slug: "legal-aid",
    name: "Legal Aid",
    description: "Legal guidance for employment, family, and property matters",
    icon: "⚖️",
    subCategories: ["Employment Law", "Family Law", "Property & Tenancy", "Will & Estate Planning"],
    sortOrder: 10,
  },
  {
    id: "cat-child-eldercare",
    slug: "child-eldercare",
    name: "Child & Eldercare",
    description: "Daycare, elder care, and family support services",
    icon: "👨‍👩‍👧",
    subCategories: ["Daycare & Creche", "Elder Care at Home", "Nursing & Assisted Living", "Special Needs Support"],
    sortOrder: 11,
  },
  {
    id: "cat-pet-care",
    slug: "pet-care",
    name: "Pet Care",
    description: "Veterinary care, grooming, and pet insurance",
    icon: "🐾",
    subCategories: ["Veterinary Consultations", "Pet Insurance", "Grooming & Boarding", "Emergency Pet Care"],
    sortOrder: 12,
  },
];

type VendorDefinition = Omit<MarketplaceVendor, "services"> & {
  services: { name: string; description: string }[];
};

export function enrichVendorPricing(vendor: VendorDefinition): MarketplaceVendor {
  const pricing = VENDOR_SERVICE_PRICING[vendor.id] || [];

  return {
    ...vendor,
    services: vendor.services.map((service, index) => ({
      ...service,
      mrp: pricing[index]?.mrp ?? 499,
      priceUnit: pricing[index]?.priceUnit ?? "session",
    })),
  };
}

const VENDOR_DEFINITIONS: VendorDefinition[] = [
  {
    id: "visit-health",
    name: "Visit Health",
    description: "All-in-one health & wellness platform for your workforce",
    longDescription:
      "Visit Health delivers OPD benefits, telemedicine, wellness rewards (FITCoins), EAP, lab tests, medicine delivery, and insurance integration — trusted by 1000+ employers across India.",
    logo: "/images/marketplace/visit-health.svg",
    countries: ["IN"],
    integrationType: "EXTERNAL_LINK",
    externalUrl: "https://getvisitapp.com/",
    subCategories: ["Wellness Programs", "Telemedicine", "EAP", "OPD Plans"],
    services: [
      { name: "OPD Cashless Claims", description: "Cashless outpatient visits across partner hospitals and clinics." },
      { name: "24×7 Teleconsultations", description: "Talk to doctors anytime via video or chat from the Visit app." },
      { name: "FITCoins Rewards", description: "Earn and redeem wellness rewards at 400+ partner brands." },
      { name: "Employee Assistance Program", description: "Emotional, financial, and legal support for employees and family." },
      { name: "Lab Tests at Home", description: "Book diagnostics with home sample collection and digital reports." },
      { name: "Medicine Delivery", description: "Order prescriptions and OTC medicines with doorstep delivery." },
      { name: "Dental & Vision OPD", description: "Outpatient dental and vision care under corporate OPD plans." },
      { name: "Insurance Integration", description: "Connect group health policies for seamless claims and coverage." },
    ],
    appDownload: {
      appName: "Visit Health",
      universalUrl: "https://getvisitapp.com/",
      iosUrl: "https://apps.apple.com/in/app/visit-health/id1436546830",
      androidUrl: "https://play.google.com/store/apps/details?id=com.visit.app",
    },
    isPlatformVendor: true,
    tags: ["Platform Partner", "OPD", "Telehealth"],
  },
  {
    id: "benefit-care-wellness",
    name: "Benefit Care Wellness",
    description: "Daily wellness videos and healthy habit programs",
    longDescription:
      "Wellness is the act of practicing healthy habits on a daily basis to attain better physical and mental health outcomes.",
    logo: "/images/benfit-care.svg",
    countries: ["IN", "AE"],
    integrationType: "EMBEDDED_UI",
    embeddedType: "wellness",
    subCategories: ["Wellness Programs", "Yoga & Meditation"],
    services: [
      { name: "Daily Wellness Videos", description: "Short expert-led videos on fitness, nutrition, and mindfulness." },
      { name: "Yoga & Meditation", description: "Guided sessions to improve flexibility, focus, and calm." },
      { name: "Healthy Habit Programs", description: "Structured challenges to build sustainable daily routines." },
      { name: "Nutrition Tips Library", description: "Practical eating guidance for busy working professionals." },
      { name: "Sleep & Recovery", description: "Content and routines to improve rest and energy levels." },
      { name: "Stress Management", description: "Techniques and programs to manage workplace stress." },
    ],
    appDownload: {
      appName: "Benefit Care",
      universalUrl: "https://www.benefitcare.in/",
    },
    tags: ["Featured"],
  },
  {
    id: "doctor-online-bee",
    name: "Doctor Online (BEE)",
    description: "24×7 online doctor consultations with qualified specialists",
    longDescription:
      "Consult top doctors across specialities from the comfort of your home. 100% safe consultations with 256-bit encryption.",
    logo: "/images/consult_specialities_img1.svg",
    countries: ["IN", "AE"],
    integrationType: "EMBEDDED_UI",
    embeddedType: "doctor-online",
    subCategories: ["General Physician", "Dermatology", "Gynaecology", "Pediatrics"],
    services: [
      { name: "General Physician", description: "Consult a GP for common illnesses, prescriptions, and follow-ups." },
      { name: "Dermatology", description: "Online consults for skin, hair, and allergy concerns." },
      { name: "Gynaecology", description: "Women's health consults from the comfort of home." },
      { name: "Pediatrics", description: "Child health guidance from qualified pediatric specialists." },
      { name: "Psychiatry", description: "Mental health support with licensed psychiatrists." },
      { name: "24×7 Chat Support", description: "Secure messaging with care coordinators around the clock." },
    ],
    appDownload: {
      appName: "Doctor Online",
      universalUrl: "https://www.benefitcare.in/",
    },
    tags: ["Featured", "24×7"],
  },
  {
    id: "quick-garage",
    name: "Quick Garage Services",
    description: "Mechanical services and periodic vehicle maintenance",
    longDescription:
      "Experienced mechanics, advance repair tools, and comprehensive car care — from periodic services to denting, painting, and roadside assistance.",
    logo: "/images/repairservice_icon.svg",
    countries: ["IN"],
    integrationType: "IN_APP_FORM",
    embeddedType: "vehicle-care",
    subCategories: ["Periodic Service", "AC Service & Repair", "Tyre & Wheel Care"],
    services: [
      { name: "Periodic Service", description: "Scheduled maintenance to keep your vehicle running smoothly." },
      { name: "AC Service & Repair", description: "Cooling system checks, gas refill, and compressor repairs." },
      { name: "Battery Replacement", description: "Testing and replacement with warranty-backed batteries." },
      { name: "Tyre & Wheel Care", description: "Rotation, balancing, alignment, and tyre replacement." },
      { name: "Denting & Painting", description: "Body repair and paint jobs with quality finish." },
      { name: "Car Spa & Cleaning", description: "Interior and exterior deep cleaning packages." },
      { name: "Roadside Assistance", description: "Breakdown support including towing and jump-start." },
    ],
    appDownload: {
      appName: "Quick Garage",
      universalUrl: "https://www.benefitcare.in/",
    },
    tags: ["Featured"],
  },
  {
    id: "cult-fit",
    name: "Cult.fit",
    description: "Gym memberships, group classes, and at-home workouts",
    longDescription: "Access premium fitness centers, live classes, and personalized workout plans.",
    logo: "https://cdn-images.cure.fit/www-curefit-com/image/upload/w_120,q_auto:good,f_auto,dpr_2,fl_progressive/image/test/brand-logo/vman-and-white-cult-text.png",
    countries: ["IN"],
    integrationType: "EXTERNAL_LINK",
    externalUrl: "https://www.cult.fit/",
    subCategories: ["Gym Memberships", "Group Classes", "At-home Fitness Apps"],
    services: [
      { name: "Gym Memberships", description: "Access premium Cult.fit centers across your city." },
      { name: "Live Group Classes", description: "Book dance, strength, HRX, and yoga sessions." },
      { name: "At-home Workouts", description: "Follow guided workouts from the Cult.fit app." },
      { name: "Personal Training", description: "One-on-one coaching for strength and conditioning goals." },
      { name: "Sports & Outdoor", description: "Badminton, swimming, and outdoor fitness experiences." },
      { name: "Cult LIVE Sessions", description: "Join live virtual classes from anywhere." },
    ],
    appDownload: {
      appName: "cult.fit",
      universalUrl: "https://www.cult.fit/",
      iosUrl: "https://apps.apple.com/in/app/cult-fit/id1459313023",
      androidUrl: "https://play.google.com/store/apps/details?id=fit.cure.android",
    },
  },
  {
    id: "practo",
    name: "Practo",
    description: "Book doctor appointments and lab tests online",
    longDescription: "India's leading healthcare platform for doctor consultations, diagnostics, and health records.",
    logo: "/images/marketplace/practo.svg",
    countries: ["IN"],
    integrationType: "EXTERNAL_LINK",
    externalUrl: "https://www.practo.com/",
    subCategories: ["General Physician", "Specialist Consultations"],
    services: [
      { name: "Doctor Appointments", description: "Book in-clinic visits with verified doctors near you." },
      { name: "Online Consultations", description: "Video consults with specialists across multiple cities." },
      { name: "Lab Test Booking", description: "Schedule diagnostics with home collection options." },
      { name: "Health Records", description: "Store and share prescriptions and reports digitally." },
      { name: "Medicine Orders", description: "Order medicines online with doorstep delivery." },
      { name: "Specialist Search", description: "Find the right specialist by symptom, rating, or location." },
    ],
    appDownload: {
      appName: "Practo",
      universalUrl: "https://www.practo.com/",
      iosUrl: "https://apps.apple.com/in/app/practo/id660583631",
      androidUrl: "https://play.google.com/store/apps/details?id=com.practo.fabric",
    },
  },
  {
    id: "yourdost",
    name: "YourDOST",
    description: "Emotional wellness and counselling support",
    longDescription: "Confidential counselling for stress, anxiety, relationships, and workplace challenges.",
    logo: "/images/consult_specialities_img5.svg",
    countries: ["IN"],
    integrationType: "EXTERNAL_LINK",
    externalUrl: "https://yourdost.com/",
    subCategories: ["Counselling & Therapy", "Employee Assistance Program"],
    services: [
      { name: "Counselling Sessions", description: "Confidential one-on-one sessions with trained psychologists." },
      { name: "Chat with Experts", description: "Text-based support for quick guidance and emotional care." },
      { name: "Emotional Wellness", description: "Programs for anxiety, burnout, and emotional balance." },
      { name: "Relationship Support", description: "Help with personal and workplace relationship challenges." },
      { name: "Workplace Stress Help", description: "Coping strategies tailored for professional pressures." },
      { name: "24×7 Crisis Support", description: "Round-the-clock access when you need immediate support." },
    ],
    appDownload: {
      appName: "YourDOST",
      universalUrl: "https://yourdost.com/",
      iosUrl: "https://apps.apple.com/in/app/yourdost/id964433856",
      androidUrl: "https://play.google.com/store/apps/details?id=com.yourdost",
    },
  },
  {
    id: "healthians",
    name: "Healthians",
    description: "Home sample collection for lab tests and checkups",
    longDescription: "NABL-accredited lab tests and full body checkups with home sample collection.",
    logo: "/images/marketplace/healthians.svg",
    countries: ["IN"],
    integrationType: "EXTERNAL_LINK",
    externalUrl: "https://www.healthians.com/",
    subCategories: ["Health Screenings"],
    services: [
      { name: "Full Body Checkup", description: "Comprehensive preventive health packages for adults." },
      { name: "Home Sample Collection", description: "Trained phlebotomists collect samples at your doorstep." },
      { name: "Diabetes Screening", description: "HbA1c, fasting glucose, and related metabolic panels." },
      { name: "Thyroid Panel", description: "TSH and thyroid function tests with fast digital reports." },
      { name: "Nutrition Panel", description: "Vitamin and nutrition markers to guide diet decisions." },
      { name: "Preventive Health Packages", description: "Curated bundles for age and gender-specific screening." },
    ],
    appDownload: {
      appName: "Healthians",
      universalUrl: "https://www.healthians.com/",
      iosUrl: "https://apps.apple.com/in/app/healthians/id1084540800",
      androidUrl: "https://play.google.com/store/apps/details?id=com.healthians.main.healthians",
    },
  },
  {
    id: "go-mechanic",
    name: "GoMechanic",
    description: "Doorstep car service and repair",
    longDescription: "Transparent pricing, free pickup & drop, and quality spare parts for all car brands.",
    logo: "/images/repair_service_icon.svg",
    countries: ["IN"],
    integrationType: "EXTERNAL_LINK",
    externalUrl: "https://gomechanic.in/",
    subCategories: ["Periodic Service", "Denting & Painting"],
    services: [
      { name: "Periodic Service", description: "Manufacturer-recommended service with transparent pricing." },
      { name: "Doorstep Pickup & Drop", description: "Free vehicle pickup and delivery for every service." },
      { name: "AC Repair", description: "Complete air-conditioning diagnostics and repairs." },
      { name: "Denting & Painting", description: "Body work and paint with quality checks at every step." },
      { name: "Battery Replacement", description: "OEM-grade batteries with warranty and installation." },
      { name: "Car Inspection", description: "Detailed health check before purchase or long trips." },
    ],
    appDownload: {
      appName: "GoMechanic",
      universalUrl: "https://gomechanic.in/",
      iosUrl: "https://apps.apple.com/in/app/gomechanic/id1459490504",
      androidUrl: "https://play.google.com/store/apps/details?id=com.gomechanic",
    },
  },
  {
    id: "lenskart",
    name: "Lenskart",
    description: "Eye exams and eyewear with corporate discounts",
    longDescription: "Free eye tests, home try-on, and subsidized spectacles and contact lenses.",
    logo: "/images/eyes.svg",
    countries: ["IN", "AE"],
    integrationType: "EXTERNAL_LINK",
    externalUrl: "https://www.lenskart.com/",
    subCategories: ["Eye Exams", "Spectacle Subsidies"],
    services: [
      { name: "Free Eye Tests", description: "Complimentary vision tests at Lenskart stores and home." },
      { name: "Spectacle Subsidies", description: "Corporate discounts on frames and prescription lenses." },
      { name: "Contact Lenses", description: "Daily and monthly lenses with expert fitting support." },
      { name: "Home Try-on", description: "Try up to multiple frames at home before you buy." },
      { name: "Blue Light Glasses", description: "Screen-protection eyewear for digital-heavy workdays." },
      { name: "Corporate Discounts", description: "Exclusive employee pricing on eyewear collections." },
    ],
    appDownload: {
      appName: "Lenskart",
      universalUrl: "https://www.lenskart.com/",
      iosUrl: "https://apps.apple.com/in/app/lenskart/id976921758",
      androidUrl: "https://play.google.com/store/apps/details?id=com.lenskart.app",
    },
  },
  {
    id: "urban-company",
    name: "Urban Company",
    description: "At-home wellness and personal care services",
    longDescription: "Book trusted professionals for salon, spa, yoga, and fitness sessions at home.",
    logo: "/images/marketplace/urban-company.png",
    countries: ["IN", "AE"],
    integrationType: "EXTERNAL_LINK",
    externalUrl: "https://www.urbancompany.com/",
    subCategories: ["Yoga & Meditation", "Personal Training"],
    services: [
      { name: "At-home Yoga", description: "Certified yoga trainers for sessions at your home." },
      { name: "Spa & Massage", description: "Relaxation and therapeutic massage by trained professionals." },
      { name: "Salon Services", description: "Hair, skin, and grooming services at your doorstep." },
      { name: "Personal Training", description: "Fitness coaching tailored to your goals and schedule." },
      { name: "Elder Care Assistance", description: "Trusted help for daily care and companionship at home." },
      { name: "Home Cleaning", description: "Deep cleaning and sanitization by verified professionals." },
    ],
    appDownload: {
      appName: "Urban Company",
      universalUrl: "https://www.urbancompany.com/",
      iosUrl: "https://apps.apple.com/in/app/urban-company/id1032480595",
      androidUrl: "https://play.google.com/store/apps/details?id=com.urbanclap.urbanclap",
    },
  },
  {
    id: "paw-insurance",
    name: "PawSecure",
    description: "Pet insurance and veterinary tele-consults",
    longDescription: "Coverage for accidents, illnesses, and routine vet visits for your pets.",
    logo: "/images/car_employee_img.svg",
    countries: ["IN"],
    integrationType: "IN_APP_FORM",
    subCategories: ["Pet Insurance", "Veterinary Consultations"],
    services: [
      { name: "Pet Insurance", description: "Coverage for accidents, illnesses, and hospitalization." },
      { name: "Vet Tele-consults", description: "Online consultations with qualified veterinarians." },
      { name: "Accident Coverage", description: "Financial protection for unexpected pet injuries." },
      { name: "Illness Coverage", description: "Support for treatment of covered medical conditions." },
      { name: "Routine Vet Visits", description: "Annual check-ups and vaccination support options." },
    ],
    appDownload: {
      appName: "PawSecure",
      universalUrl: "https://www.benefitcare.in/",
    },
  },
];

export const VENDORS: MarketplaceVendor[] = VENDOR_DEFINITIONS.map(enrichVendorPricing);

export const CATEGORY_VENDOR_LISTINGS: CategoryVendorListing[] = [
  { categoryId: "cat-wellness", vendorId: "visit-health", highlight: "Integrated wellness platform — OPD, rewards & more" },
  { categoryId: "cat-wellness", vendorId: "benefit-care-wellness", highlight: "Corporate wellness video library" },
  { categoryId: "cat-wellness", vendorId: "healthians", highlight: "Full body checkups at home" },
  { categoryId: "cat-wellness", vendorId: "urban-company", highlight: "At-home spa & yoga sessions" },
  { categoryId: "cat-fitness", vendorId: "visit-health", highlight: "FITCoins rewards at 400+ brands" },
  { categoryId: "cat-fitness", vendorId: "cult-fit", highlight: "Premium gyms & live classes" },
  { categoryId: "cat-fitness", vendorId: "urban-company", highlight: "Personal training at home" },
  { categoryId: "cat-telehealth", vendorId: "visit-health", highlight: "Consult doctors 24×7 via Visit app" },
  { categoryId: "cat-telehealth", vendorId: "doctor-online-bee", highlight: "In-platform doctor consultations" },
  { categoryId: "cat-telehealth", vendorId: "practo", highlight: "Book in-clinic & online appointments" },
  { categoryId: "cat-vehicle-care", vendorId: "quick-garage", highlight: "Request service in-app" },
  { categoryId: "cat-vehicle-care", vendorId: "go-mechanic", highlight: "Doorstep pickup & drop service" },
  { categoryId: "cat-mental-health", vendorId: "visit-health", highlight: "EAP — emotional wellness pillar" },
  { categoryId: "cat-mental-health", vendorId: "yourdost", highlight: "Confidential counselling sessions" },
  { categoryId: "cat-nutrition", vendorId: "healthians", highlight: "Nutrition panels & diet screening" },
  { categoryId: "cat-dental", vendorId: "visit-health", highlight: "Dental services via OPD plans" },
  { categoryId: "cat-vision", vendorId: "visit-health", highlight: "Vision services via OPD plans" },
  { categoryId: "cat-vision", vendorId: "lenskart", highlight: "Corporate eyewear discounts" },
  { categoryId: "cat-financial", vendorId: "visit-health", highlight: "EAP — financial wellness pillar" },
  { categoryId: "cat-legal", vendorId: "visit-health", highlight: "EAP — legal guidance pillar" },
  { categoryId: "cat-child-eldercare", vendorId: "urban-company", highlight: "At-home elder care assistance" },
  { categoryId: "cat-pet-care", vendorId: "paw-insurance", highlight: "Pet insurance & vet consults" },
];

export function getCategoryById(id: string): MarketplaceCategory | undefined {
  return CATEGORIES.find((c) => c.id === id || c.slug === id);
}

export function getVendorById(id: string): MarketplaceVendor | undefined {
  return VENDORS.find((v) => v.id === id);
}

export function getVendorsForCategory(
  categoryId: string,
  country: CountryCode
): (MarketplaceVendor & { highlight: string })[] {
  const listings = CATEGORY_VENDOR_LISTINGS.filter((l) => l.categoryId === categoryId);
  return listings
    .map((listing) => {
      const vendor = getVendorById(listing.vendorId);
      if (!vendor || !vendor.countries.includes(country)) return null;
      return { ...vendor, highlight: listing.highlight };
    })
    .filter(Boolean) as (MarketplaceVendor & { highlight: string })[];
}

export function getVendorCountForCategory(categoryId: string, country: CountryCode): number {
  return getVendorsForCategory(categoryId, country).length;
}

export function getListingForVendorInCategory(
  categoryId: string,
  vendorId: string
): CategoryVendorListing | undefined {
  return CATEGORY_VENDOR_LISTINGS.find(
    (l) => l.categoryId === categoryId && l.vendorId === vendorId
  );
}
